import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import './Modal.css';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    className?: string;
    closeButtonLabel?: string;
    closeOnOverlayClick?: boolean;
    ariaLabelledBy?: string;
    ariaDescribedBy?: string;
    showCloseButton?: boolean;
}

const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    children,
    className = '',
    closeButtonLabel = 'Close dialog',
    closeOnOverlayClick = true,
    ariaLabelledBy,
    ariaDescribedBy,
    showCloseButton = true
}) => {
    const [mouseDownTarget, setMouseDownTarget] = React.useState<EventTarget | null>(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const handleOverlayMouseDown = (event: React.MouseEvent) => {
        setMouseDownTarget(event.target);
    };

    const handleOverlayClick = (event: React.MouseEvent) => {
        // Only close if both mousedown and mouseup happened on the overlay
        if (closeOnOverlayClick && event.target === mouseDownTarget && event.target === event.currentTarget) {
            onClose();
        }
        setMouseDownTarget(null);
    };

    const modalContent = (
        <div
            className="app-modal-overlay"
            role="presentation"
            onMouseDown={handleOverlayMouseDown}
            onClick={handleOverlayClick}
        >
            <div
                className={`app-modal-container ${className}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby={ariaLabelledBy}
                aria-describedby={ariaDescribedBy}
                onClick={(event) => event.stopPropagation()}
            >
                {showCloseButton && (
                    <button
                        type="button"
                        className="app-modal-close-button"
                        onClick={onClose}
                        aria-label={closeButtonLabel}
                    >
                        ×
                    </button>
                )}
                <div className="app-modal-content">
                    {children}
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modalContent, document.body);
};

export default Modal;
